<template>
  <div>
    <div class="d-flex align-items-center gap-2 mb-2">
      <div class="input-group" style="max-width:280px">
        <span class="input-group-text"><i class="bi bi-search"></i></span>
        <input
          :value="search"
          @input="$emit('update:search', $event.target.value)"
          type="search"
          class="form-control form-control-sm"
          placeholder="Filter resources…"
        />
      </div>
      <span class="text-muted small ms-auto">{{ totalCount }} resources</span>
    </div>

    <div v-if="!grouped.length" class="text-muted small py-2">No resources match.</div>
    <div v-else class="border rounded overflow-auto">
      <table class="table table-sm table-hover mb-0 small">
        <thead class="table-light">
          <tr>
            <th>Resource</th>
            <th class="text-end">Daily Used</th>
            <th class="text-end">Daily Limit</th>
            <th style="min-width:120px">Daily %</th>
            <th class="text-end">Resets</th>
            <th class="text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="g in grouped" :key="g.key">
            <td>
              <!-- Single resource: render as before -->
              <template v-if="g.members.length === 1">
                <div class="font-monospace">{{ g.members[0].name }}</div>
                <div class="text-muted" style="font-size:0.75em">{{ g.members[0].apiContext }} · {{ g.members[0].apiName }}</div>
              </template>
              <!-- Grouped resources sharing the same quota pool -->
              <template v-else>
                <div class="d-flex align-items-center gap-1">
                  <button class="btn btn-link btn-sm p-0 lh-1 text-decoration-none text-secondary" @click="toggleGroup(g.key)">
                    <i :class="expanded.has(g.key) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                  </button>
                  <span class="fw-semibold">{{ g.apiContext }}</span>
                  <span class="badge bg-secondary rounded-pill ms-1" style="font-size:0.7em">{{ g.members.length }}</span>
                </div>
                <!-- Expanded: list every resource name -->
                <div v-if="expanded.has(g.key)" class="mt-1 ps-3">
                  <div v-for="m in g.members" :key="m.name" class="font-monospace text-muted" style="font-size:0.8em">{{ m.name }}</div>
                </div>
                <!-- Collapsed: truncated preview -->
                <div v-else class="text-muted ps-3 mt-1" style="font-size:0.75em">
                  {{ preview(g.members) }}
                </div>
              </template>
            </td>
            <td class="text-end align-middle">{{ g.daily ? g.daily.used.toLocaleString() : '—' }}</td>
            <td class="text-end align-middle text-muted">{{ g.daily ? g.daily.limit.toLocaleString() : '—' }}</td>
            <td class="align-middle">
              <template v-if="g.daily">
                <div class="progress mb-1" style="height:6px">
                  <div
                    class="progress-bar"
                    :class="barClass(g.daily.used, g.daily.limit)"
                    :style="{ width: pct(g.daily.used, g.daily.limit) + '%' }"
                  ></div>
                </div>
                <div class="text-muted" style="font-size:0.75em">{{ pct(g.daily.used, g.daily.limit) }}%</div>
              </template>
              <span v-else class="text-muted">—</span>
            </td>
            <td class="text-end align-middle text-muted" style="font-size:0.8em">{{ fmtReset(g.daily?.reset) }}</td>
            <td class="text-center align-middle">
              <span class="badge" :class="statusClass(g)">{{ statusLabel(g) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'

const props = defineProps({
  resources: { type: Array, default: () => [] },
  search:    { type: String, default: '' },
})
defineEmits(['update:search'])

const expanded = ref(new Set())

function toggleGroup(key) {
  const next = new Set(expanded.value)
  next.has(key) ? next.delete(key) : next.add(key)
  expanded.value = next
}

function pct(used, limit) {
  if (!limit) return 0
  return Math.min(100, Math.round((used / limit) * 100))
}

function barClass(used, limit) {
  const p = pct(used, limit)
  if (p >= 100) return 'bg-danger'
  if (p >= 80)  return 'bg-warning'
  return 'bg-success'
}

function statusLabel(g) {
  if (g.blocked) return 'BLOCKED'
  if (g.daily && pct(g.daily.used, g.daily.limit) >= 80) return 'WARNING'
  return 'OK'
}

function statusClass(g) {
  if (g.blocked) return 'bg-danger'
  if (g.daily && pct(g.daily.used, g.daily.limit) >= 80) return 'bg-warning text-dark'
  return 'bg-success'
}

function fmtReset(isoStr) {
  if (!isoStr) return '—'
  return new Date(isoStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
}

function preview(members) {
  const names = members.map(m => m.name).join(', ')
  return names.length > 80 ? names.slice(0, 80) + '…' : names
}

const grouped = computed(() => {
  const term = props.search.trim().toLowerCase()
  const filtered = props.resources.filter(r =>
    !term ||
    r.name.toLowerCase().includes(term) ||
    r.apiName?.toLowerCase().includes(term) ||
    r.apiContext?.toLowerCase().includes(term)
  )

  const map = new Map()
  for (const r of filtered) {
    const key = `${r.apiContext?.trim()}::${r.daily?.limit ?? 'null'}::${r.daily?.used ?? 'null'}`
    if (!map.has(key)) {
      map.set(key, { key, apiContext: r.apiContext?.trim(), daily: r.daily, blocked: false, members: [] })
    }
    const g = map.get(key)
    g.members.push(r)
    if (r.blocked) g.blocked = true
  }

  return [...map.values()].sort((a, b) => {
    const pa = a.daily ? pct(a.daily.used, a.daily.limit) : 0
    const pb = b.daily ? pct(b.daily.used, b.daily.limit) : 0
    return pb - pa
  })
})

const totalCount = computed(() => grouped.value.reduce((s, g) => s + g.members.length, 0))
</script>
